
import { createContext, useContext, useState, ReactNode, useEffect } from 'react';

interface EarlyAdopterContextType {
  isModalOpen: boolean;
  openModal: (source?: string) => void;
  closeModal: () => void;
  sourcePage: string;
}

const EarlyAdopterContext = createContext<EarlyAdopterContextType | undefined>(undefined);

export const useEarlyAdopter = () => {
  const context = useContext(EarlyAdopterContext);
  
  // Provide fallback when provider is not available (for debugging purposes)
  if (!context) {
    console.error('🚨 useEarlyAdopter used without EarlyAdopterProvider - this should not happen!');
    console.error('🚨 Stack trace:', new Error().stack);
    return {
      isModalOpen: false,
      openModal: (source?: string) => {
        console.error('❌ EarlyAdopter modal cannot open - no provider:', source);
      },
      closeModal: () => {
        console.error('❌ EarlyAdopter modal cannot close - no provider');
      },
      sourcePage: ''
    };
  }
  
  console.log('✅ EarlyAdopter context found successfully');
  return context;
};

interface EarlyAdopterProviderProps {
  children: ReactNode;
}

export const EarlyAdopterProvider = ({ children }: EarlyAdopterProviderProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sourcePage, setSourcePage] = useState('');
  const [userHasClosed, setUserHasClosed] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  // Check for URL parameters on mount and route changes
  useEffect(() => {
    const checkURLParameters = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const hash = window.location.hash;
      const pathname = window.location.pathname;
      
      console.log('🔍 EarlyAdopterContext checking URL:', { pathname, hash, urlParams: urlParams.toString() });
      
      // Reset userHasClosed flag when navigating to a different path
      if (pathname !== currentPath) {
        console.log('🔄 Path changed from', currentPath, 'to', pathname, '- resetting userHasClosed flag');
        setUserHasClosed(false);
        setCurrentPath(pathname);
      }
      
      // Check if we're on a dedicated early access route
      const isDedicatedRoute = pathname === '/early-access' || pathname === '/get-vip' || pathname === '/vip';
      
      // Check for various parameter combinations that should open the modal
      const shouldOpenModal = 
        isDedicatedRoute ||
        urlParams.get('modal') === 'early-access' ||
        urlParams.get('signup') === 'true' ||
        urlParams.get('vip') === 'true' ||
        hash === '#early-access' ||
        hash === '#signup' ||
        hash === '#vip';

      console.log('🎯 Should open modal:', shouldOpenModal, 'Is dedicated route:', isDedicatedRoute, 'Current modal state:', isModalOpen, 'User has closed:', userHasClosed);

      // Only open modal if it should be open AND it's not already open AND user hasn't explicitly closed it
      if (shouldOpenModal && !isModalOpen && !userHasClosed) {
        const source = determineSource(urlParams, hash, pathname);
        console.log('🚀 Opening modal from source:', source);
        openModal(source);
        
        // Only clean up URL parameters for non-dedicated routes
        if (!isDedicatedRoute) {
          cleanupURL(urlParams, hash);
        }
      }
    };

    const determineSource = (urlParams: URLSearchParams, hash: string, pathname: string) => {
      if (pathname === '/early-access') return 'Direct Route - /early-access';
      if (pathname === '/get-vip') return 'Direct Route - /get-vip';
      if (pathname === '/vip') return 'Direct Route - /vip';
      if (urlParams.get('modal') === 'early-access') return 'Direct Link - Modal Parameter';
      if (urlParams.get('signup') === 'true') return 'Direct Link - Signup Parameter';
      if (urlParams.get('vip') === 'true') return 'Direct Link - VIP Parameter';
      if (hash === '#early-access') return 'Direct Link - Early Access Hash';
      if (hash === '#signup') return 'Direct Link - Signup Hash';
      if (hash === '#vip') return 'Direct Link - VIP Hash';
      return 'Direct Link - Auto Open';
    };

    const cleanupURL = (urlParams: URLSearchParams, hash: string) => {
      let needsCleanup = false;
      
      console.log('🧹 Cleaning up URL parameters');
      
      // Remove modal-related parameters
      if (urlParams.has('modal') || urlParams.has('signup') || urlParams.has('vip')) {
        urlParams.delete('modal');
        urlParams.delete('signup');
        urlParams.delete('vip');
        needsCleanup = true;
      }

      // Clean up hash
      if (hash === '#early-access' || hash === '#signup' || hash === '#vip') {
        needsCleanup = true;
      }

      if (needsCleanup) {
        const newURL = window.location.pathname + 
          (urlParams.toString() ? '?' + urlParams.toString() : '') +
          (hash && !['#early-access', '#signup', '#vip'].includes(hash) ? hash : '');
        
        console.log('🔄 Updating URL to:', newURL);
        window.history.replaceState({}, '', newURL);
      }
    };

    // Check on initial load
    checkURLParameters();

    // Listen for route changes (for SPAs)
    const handlePopState = () => {
      console.log('🔄 Route changed, rechecking URL parameters');
      checkURLParameters();
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isModalOpen, userHasClosed, currentPath]);

  const openModal = (source = window.location.pathname) => {
    console.log('📝 EarlyAdopterContext: Opening modal with source:', source, 'Current state:', isModalOpen);
    
    // Prevent multiple opens
    if (isModalOpen) {
      console.log('⚠️ Modal is already open, skipping duplicate open request');
      return;
    }
    
    setSourcePage(source);
    setIsModalOpen(true);
    console.log('✅ Modal state set to open');
  };

  const closeModal = () => {
    console.log('🔐 EarlyAdopterContext: Closing modal, current state:', isModalOpen);
    setIsModalOpen(false);
    setUserHasClosed(true); // Mark that user has explicitly closed the modal
    console.log('✅ Modal state set to closed, userHasClosed set to true');
  };

  const contextValue = {
    isModalOpen,
    openModal,
    closeModal,
    sourcePage
  };

  console.log('🔄 EarlyAdopterProvider rendering with modal state:', isModalOpen, 'userHasClosed:', userHasClosed);

  return (
    <EarlyAdopterContext.Provider value={contextValue}>
      {children}
    </EarlyAdopterContext.Provider>
  );
};
